import React, { useState, useMemo } from 'react';
import { FiPlus, FiTrash2, FiArrowLeft, FiZap, FiCpu } from 'react-icons/fi';
import { AI_MODELS, DEFAULT_AGENT_MODEL } from '@troia-v3/types';

import type { ABTestVariantInput } from '../../services/abTestApi';

// ─── Helpers ─────────────────────────────────────────────────────────────

const CREDITS_PER_EXECUTION = 1;

// TEMPORÁRIO (23/10/2026): só o GPT-6 Luna está liberado. Para voltar:
// const availableModels = AI_MODELS.filter((m) => !m.deprecated);
const availableModels = AI_MODELS.filter((m) => !m.deprecated && m.id === DEFAULT_AGENT_MODEL);

function formatModelLabel(model: (typeof availableModels)[number]): string {
  return `${model.name} (${model.provider}) — $${model.pricing.input}/$${model.pricing.output} por 1M tokens`;
}

function createVariant(modelId?: string): ABTestVariantInput {
  return {
    id: crypto.randomUUID(),
    model: modelId ?? availableModels[0]?.id ?? 'gpt-4.1-nano',
    temperature: 0.7,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────

interface ABTestConfigStepProps {
  onStart: (variants: ABTestVariantInput[], scenarioType: 'quick' | 'full') => void;
  onBack: () => void;
  isStarting: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────

export const ABTestConfigStep: React.FC<ABTestConfigStepProps> = ({
  onStart,
  onBack,
  isStarting,
}) => {
  const [variants, setVariants] = useState<ABTestVariantInput[]>([
    createVariant(availableModels[0]?.id),
    createVariant(availableModels[1]?.id),
  ]);
  const [scenarioType, setScenarioType] = useState<'quick' | 'full'>('quick');

  const scenarioCount = scenarioType === 'quick' ? 5 : 12;
  const totalExecutions = variants.length * scenarioCount;
  const estimatedCredits = totalExecutions * CREDITS_PER_EXECUTION;
  const canStart = variants.length >= 2 && !isStarting;

  const updateVariant = (id: string, patch: Partial<ABTestVariantInput>) => {
    setVariants((prev) =>
      prev.map((v) => (v.id === id ? { ...v, ...patch } : v)),
    );
  };

  const removeVariant = (id: string) => {
    setVariants((prev) => prev.filter((v) => v.id !== id));
  };

  const addVariant = () => {
    const usedModels = new Set(variants.map((v) => v.model));
    const nextModel = availableModels.find((m) => !usedModels.has(m.id));
    setVariants((prev) => [...prev, createVariant(nextModel?.id)]);
  };

  // Memoize model options for select
  const modelOptions = useMemo(
    () =>
      availableModels.map((m) => ({
        value: m.id,
        label: formatModelLabel(m),
      })),
    [],
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <div
            className="p-1.5 rounded-md"
            style={{
              background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
              color: '#ffffff',
            }}
          >
            <FiCpu className="h-5 w-5" />
          </div>
          <h2
            className="text-lg font-semibold"
            style={{ color: '#1e293b' }}
          >
            Comparar Modelos
          </h2>
        </div>
        <p className="text-sm" style={{ color: '#6b7280' }}>
          Configure as variantes para comparação
        </p>
      </div>

      {/* Variant list */}
      <div className="space-y-3 mb-4">
        {variants.map((variant, index) => (
          <div
            key={variant.id}
            className="rounded-lg border p-4"
            style={{
              backgroundColor: '#ffffff',
              borderColor: 'rgba(0, 0, 0, 0.08)',
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <span
                className="text-sm font-semibold"
                style={{ color: '#1e293b' }}
              >
                Variante {index + 1}
              </span>
              {variants.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeVariant(variant.id)}
                  className="p-1.5 rounded-md transition-colors"
                  style={{ color: '#94a3b8' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#ef4444';
                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#94a3b8';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  aria-label={`Remover variante ${index + 1}`}
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Model select */}
            <div className="mb-3">
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: '#64748b' }}
              >
                Modelo
              </label>
              <select
                value={variant.model}
                onChange={(e) => updateVariant(variant.id, { model: e.target.value })}
                className="w-full h-[38px] rounded-md border px-3 py-2 text-sm"
                style={{
                  backgroundColor: '#ffffff',
                  borderColor: 'rgba(0, 0, 0, 0.10)',
                  color: '#1e293b',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#8b5cf6';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.10)';
                }}
              >
                {modelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Temperature slider */}
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: '#64748b' }}
              >
                Temperatura:{' '}
                <span className="font-semibold" style={{ color: '#8b5cf6' }}>
                  {variant.temperature.toFixed(1)}
                </span>
              </label>
              <input
                type="range"
                min={0}
                max={2}
                step={0.1}
                value={variant.temperature}
                onChange={(e) =>
                  updateVariant(variant.id, {
                    temperature: Number(e.target.value),
                  })
                }
                className="w-full accent-violet-500"
                aria-label={`Temperatura da variante ${index + 1}`}
              />
              <div className="flex justify-between text-xs mt-0.5" style={{ color: '#94a3b8' }}>
                <span>0 (preciso)</span>
                <span>2 (criativo)</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add variant button */}
      <button
        type="button"
        onClick={addVariant}
        className="flex items-center gap-1.5 text-sm font-medium mb-5 transition-colors"
        style={{ color: '#8b5cf6' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#7c3aed';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#8b5cf6';
        }}
      >
        <FiPlus className="h-4 w-4" />
        Adicionar variante
      </button>

      {/* Scenario type selector */}
      <div className="mb-4">
        <label
          className="text-sm font-medium block mb-2"
          style={{ color: '#1e293b' }}
        >
          Tipo de cenário
        </label>
        <div className="flex gap-3">
          <ScenarioOption
            selected={scenarioType === 'quick'}
            onClick={() => setScenarioType('quick')}
            icon={<FiZap className="h-4 w-4" />}
            label="Rápido (5 cenários)"
          />
          <ScenarioOption
            selected={scenarioType === 'full'}
            onClick={() => setScenarioType('full')}
            icon={<FiCpu className="h-4 w-4" />}
            label="Completo (12 cenários)"
          />
        </div>
      </div>

      {/* Cost estimate */}
      <div
        className="rounded-lg border p-3 mb-5"
        style={{
          backgroundColor: 'rgba(139, 92, 246, 0.04)',
          borderColor: 'rgba(139, 92, 246, 0.15)',
        }}
      >
        <p className="text-sm" style={{ color: '#64748b' }}>
          <span className="font-semibold" style={{ color: '#8b5cf6' }}>
            {variants.length} variantes
          </span>
          {' × '}
          <span className="font-semibold" style={{ color: '#8b5cf6' }}>
            {scenarioCount} cenários
          </span>
          {' = '}
          <span className="font-semibold" style={{ color: '#1e293b' }}>
            {totalExecutions} execuções
          </span>
          {' · ~'}
          <span className="font-semibold" style={{ color: '#8b5cf6' }}>
            {estimatedCredits} créditos
          </span>
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={isStarting}
          className="flex items-center gap-1.5 text-sm font-medium transition-colors"
          style={{ color: '#64748b' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#64748b';
          }}
        >
          <FiArrowLeft className="h-4 w-4" />
          Voltar
        </button>

        <button
          type="button"
          onClick={() => onStart(variants, scenarioType)}
          disabled={!canStart}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
          style={{
            background: canStart
              ? 'linear-gradient(90deg, #8b5cf6, #a78bfa)'
              : '#d1d5db',
            cursor: canStart ? 'pointer' : 'not-allowed',
            opacity: canStart ? 1 : 0.7,
          }}
          onMouseEnter={(e) => {
            if (canStart) {
              e.currentTarget.style.background =
                'linear-gradient(90deg, #7c3aed, #8b5cf6)';
            }
          }}
          onMouseLeave={(e) => {
            if (canStart) {
              e.currentTarget.style.background =
                'linear-gradient(90deg, #8b5cf6, #a78bfa)';
            }
          }}
        >
          {isStarting ? 'Iniciando...' : 'Iniciar Comparação'}
        </button>
      </div>
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────

interface ScenarioOptionProps {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

const ScenarioOption: React.FC<ScenarioOptionProps> = ({
  selected,
  onClick,
  icon,
  label,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors"
    style={{
      backgroundColor: selected ? 'rgba(139, 92, 246, 0.08)' : '#ffffff',
      borderColor: selected ? '#8b5cf6' : 'rgba(0, 0, 0, 0.08)',
      color: selected ? '#8b5cf6' : '#64748b',
    }}
    onMouseEnter={(e) => {
      if (!selected) {
        e.currentTarget.style.borderColor = '#8b5cf6';
        e.currentTarget.style.color = '#8b5cf6';
      }
    }}
    onMouseLeave={(e) => {
      if (!selected) {
        e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
        e.currentTarget.style.color = '#64748b';
      }
    }}
  >
    {icon}
    {label}
  </button>
);
